export const schema = {
  id: 'chp-commissioning-servicing-record',
  name: 'Combined Heat & Power (CHP) Commissioning & Servicing Record',
  description: 'Commercial Cogeneration / Micro-CHP Commissioning, G99 Grid Interface & Periodic Maintenance Record',
  category: 'HVAC / Renewables',
  sections: [
    {
      id: 'contractorDetails',
      title: '1. Commissioning Contractor & Specialist Engineer',
      fields: [
        { id: 'companyName', label: 'Company Name', type: 'text', required: true },
        { id: 'engineerName', label: 'Lead Specialist Engineer', type: 'text', required: true },
        { id: 'gasSafeLicence', label: 'Gas Safe Commercial (COCN1/ICPN1) / Competency ID', type: 'text', required: true },
        { id: 'telephone', label: 'Telephone Number', type: 'tel', required: true }
      ]
    },
    {
      id: 'siteDetails',
      title: '2. Plant Room & Facility Details',
      fields: [
        { id: 'siteName', label: 'Site / Facility Name', type: 'text', required: true },
        { id: 'plantRoomLocation', label: 'Plant Room Location / Level', type: 'text', required: true },
        { id: 'siteAddress', label: 'Site Address', type: 'textarea', required: true },
        { id: 'postCode', label: 'Postcode', type: 'text', required: true },
        { id: 'serviceDate', label: 'Date of Service / Commissioning', type: 'date', required: true }
      ]
    },
    {
      id: 'chpSpecification',
      title: '3. Cogeneration Unit Specification',
      fields: [
        { id: 'manufacturer', label: 'CHP Manufacturer (e.g. Tedom, EC Power, Ener-G, Viessmann)', type: 'text', required: true },
        { id: 'model', label: 'Model Number', type: 'text', required: true },
        { id: 'serialNumber', label: 'Serial Number', type: 'text', required: true },
        { id: 'fuelType', label: 'Fuel Type', type: 'select', options: ['Natural Gas (Methane)', 'LPG (Propane)', 'Biogas / Syngas', 'Dual Fuel'], required: true },
        { id: 'electricalOutputKwe', label: 'Rated Electrical Output (kWe)', type: 'number', required: true },
        { id: 'thermalOutputKwt', label: 'Rated Thermal Heat Output (kWt)', type: 'number', required: true },
        { id: 'operatingHoursTotal', label: 'Total Operating Engine Hours', type: 'number', required: true }
      ]
    },
    {
      id: 'engineAndLubrication',
      title: '4. Internal Combustion Engine & Lubrication Service',
      fields: [
        { id: 'oilSampleTaken', label: 'Lubricating Oil Sample Taken for Laboratory Analysis?', type: 'select', options: ['Yes', 'No', 'N/A'], required: true },
        { id: 'oilReplaced', label: 'Engine Oil Replaced?', type: 'select', options: ['Yes - Drained & Refilled', 'Topped Up Only', 'Checked Satisfactory'], required: true },
        { id: 'oilFilterReplaced', label: 'Oil Filter Cartridge Replaced?', type: 'select', options: ['Yes', 'No', 'N/A'], required: true },
        { id: 'sparkPlugsGappedOrReplaced', label: 'Spark Plugs Inspected / Replaced & Gapped?', type: 'select', options: ['Replaced & Gapped', 'Cleaned & Gapped', 'N/A - Diesel'], required: true },
        { id: 'valveClearancesChecked', label: 'Engine Valve Tappet Clearances Verified?', type: 'select', options: ['Yes - Adjusted', 'Yes - Within Spec', 'No'], required: true },
        { id: 'crankcaseBreatherCleaned', label: 'Crankcase Breather / Blowby Filter Cleaned?', type: 'select', options: ['Yes', 'No'], required: true }
      ]
    },
    {
      id: 'gasAndCombustion',
      title: '5. Gas Train, Lambda & Flue Emissions Analysis',
      fields: [
        { id: 'standingPressureMbar', label: 'Gas Supply Standing Pressure (mbar)', type: 'number', required: true },
        { id: 'runningPressureMbar', label: 'Gas Supply Working Pressure (mbar)', type: 'number', required: true },
        { id: 'lambdaSensorVoltage', label: 'Lambda Sensor Output Voltage / O2 %', type: 'number', required: true },
        { id: 'flueCoPpm', label: 'Flue Gas CO (ppm @ 5% O2)', type: 'number', required: true },
        { id: 'flueNoxPpm', label: 'Flue Gas NOx (mg/Nm³ or ppm)', type: 'number', required: true },
        { id: 'flueGasTempC', label: 'Exhaust Gas Temperature (°C)', type: 'number', required: true },
        { id: 'flueExhaustSystemIntegrity', label: 'Exhaust Dilution & Flue Integrity Sound?', type: 'select', options: ['Pass / Gas Tight', 'Failed'], required: true }
      ]
    },
    {
      id: 'electricalGridProtection',
      title: '6. ENA EREC G99 Grid Loss of Mains Protection & Interlocks',
      fields: [
        { id: 'g99ProtectionRelayTested', label: 'G99 Loss of Mains (RoCoF / Vector Shift) Relay Tested?', type: 'select', options: ['Yes - Secondary Injection Test Passed', 'Yes - Functional Trip Tested', 'N/A - Off-Grid'], required: true },
        { id: 'underVoltageTripSec', label: 'Under/Over Voltage Trip Time (ms)', type: 'number' },
        { id: 'underFrequencyTripSec', label: 'Under/Over Frequency Trip Time (ms)', type: 'number' },
        { id: 'emergencyStopTested', label: 'Plant Room Emergency Stop Interlocks Tested?', type: 'select', options: ['Pass / Immediate Trip', 'Failed'], required: true },
        { id: 'gasSolenoidInterlockTested', label: 'Gas Safety Solenoid Valve Interlock Verified?', type: 'select', options: ['Pass / Shuts on Alarm', 'Failed'], required: true },
        { id: 'bmsModbusCommActive', label: 'BMS / Remote Monitoring Telemetry Active?', type: 'select', options: ['Yes', 'No'], required: true }
      ]
    },
    {
      id: 'declarations',
      title: '7. Overall Assessment & Handover',
      fields: [
        { id: 'overallStatus', label: 'Overall Unit Operational Status', type: 'select', options: ['COMMISSIONED & FULLY OPERATIONAL', 'SERVICED & RUNNING IN BASELOAD', 'DEFECTIVE / ISOLATED (ACTION REQUIRED)'], required: true },
        { id: 'engineerComments', label: 'Engineer Observations & Remedial Recommendations', type: 'textarea' },
        { id: 'nextServiceDueHours', label: 'Next Service Due at Operating Hours / Date', type: 'text', required: true },
        { id: 'engineerSignature', label: 'Specialist Engineer Signature', type: 'signature', required: true },
        { id: 'clientSignature', label: 'Plant Manager / Client Signature', type: 'signature', required: true }
      ]
    }
  ]
};

export default schema;
